import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Clock } from 'lucide-react'
import Shell from '../components/Shell'
import { useAuth } from '../contexts/AuthContext'
import { functionsClient } from '../lib/functionsClient'
import styles from './NewCasePage.module.css'

// Supported loan programs (the ones we can actually process)
const SUPPORTED_PROGRAMS = ['fannie_mae', 'freddie_mac']

// Display info for loan programs with proper capitalization and descriptions
const PROGRAM_DISPLAY_INFO: Record<string, { name: string; description: string }> = {
  fannie_mae: {
    name: 'Fannie Mae',
    description: 'Conventional conforming loans backed by Fannie Mae',
  },
  freddie_mac: {
    name: 'Freddie Mac',
    description: 'Conventional conforming loans backed by Freddie Mac',
  },
  fha: {
    name: 'FHA',
    description: 'Federal Housing Administration insured loans',
  },
  va: {
    name: 'VA',
    description: 'Department of Veterans Affairs guaranteed loans',
  },
  usda: {
    name: 'USDA',
    description: 'Rural development loans for eligible areas',
  },
  jumbo: {
    name: 'Jumbo',
    description: 'Non-conforming loans exceeding conforming limits',
  },
  non_qm: {
    name: 'Non-QM',
    description: 'Non-qualified mortgage for unique situations',
  },
}

interface LoanProgram {
  value: string
  label: string
  description?: string
  enabled?: boolean
}

const NewCasePage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [borrowerName, setBorrowerName] = useState('')
  const [selectedProgram, setSelectedProgram] = useState<string>('fannie_mae')
  const [loanPrograms, setLoanPrograms] = useState<LoanProgram[]>([])
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch loan programs from backend
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setIsLoadingPrograms(true)
        const response = await functionsClient.getLoanProgramOptions()
        setLoanPrograms(response.loan_programs)
      } catch (err) {
        console.error('Failed to load loan programs:', err)
        // Fallback programs
        const fallback: LoanProgram[] = [
          { value: 'fannie_mae', label: 'Fannie Mae', enabled: true },
          { value: 'freddie_mac', label: 'Freddie Mac', enabled: true },
          { value: 'fha', label: 'FHA', enabled: false },
          { value: 'va', label: 'VA', enabled: false },
          { value: 'usda', label: 'USDA', enabled: false },
          { value: 'jumbo', label: 'Jumbo', enabled: false },
          { value: 'non_qm', label: 'Non-QM', enabled: false },
        ]
        setLoanPrograms(fallback)
      } finally {
        setIsLoadingPrograms(false)
      }
    }
    fetchPrograms()
  }, [])

  const handleBack = () => {
    navigate('/quick-check')
  }

  const handleProgramClick = (programValue: string) => {
    const isSupported = SUPPORTED_PROGRAMS.includes(programValue)
    if (isSupported) {
      setSelectedProgram(programValue)
      setError(null)
    } else {
      // Show coming soon message
      const displayInfo = PROGRAM_DISPLAY_INFO[programValue]
      const programName = displayInfo?.name || programValue
      setError(`${programName} support is coming soon. Please select Fannie Mae or Freddie Mac for now.`)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!borrowerName.trim()) {
      setError('Please enter a borrower name')
      return
    }

    if (!selectedProgram) {
      setError('Please select a loan program')
      return
    }

    if (!SUPPORTED_PROGRAMS.includes(selectedProgram)) {
      setError('Please select a supported loan program (Fannie Mae or Freddie Mac)')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const borrowerInfo = {
        full_name: borrowerName.trim(),
        email: user?.email || 'pending@example.com',
        phone: '000-000-0000',
        employment_status: 'employed' as const,
        income_type: 'Unknown' as const,
        annual_income: 0,
        monthly_debts: 0,
        loan_type: selectedProgram,
        loan_purpose: 'purchase' as const,
        estimated_loan_amount: 300000,
        location_city: 'Unknown',
        location_state: 'Unknown',
      }

      const result = await functionsClient.submitCase(borrowerInfo)

      if (result.case_id) {
        navigate(`/quick-check/${result.case_id}/documents`)
      }
    } catch (err) {
      console.error('Failed to create case:', err)
      setError(err instanceof Error ? err.message : 'Failed to create case. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getDisplayInfo = (program: LoanProgram) => {
    const info = PROGRAM_DISPLAY_INFO[program.value]
    return {
      name: info?.name || program.label,
      description: info?.description || program.description || '',
    }
  }

  const isSupported = (programValue: string) => SUPPORTED_PROGRAMS.includes(programValue)

  return (
    <Shell variant="app" showFooter={false}>
      <div className={styles.container}>
        <div className={styles.content}>
          {/* Back Button */}
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowLeft size={18} />
            Back to Cases
          </button>

          {/* Header */}
          <div className={styles.header}>
            <h1 className={styles.title}>Create New Case</h1>
            <p className={styles.subtitle}>
              Enter borrower details to start a new verification case
            </p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Borrower Name */}
            <div className={styles.fieldGroup}>
              <label htmlFor="borrowerName" className={styles.label}>
                Borrower Name
              </label>
              <input
                id="borrowerName"
                type="text"
                className={styles.input}
                placeholder="e.g., Maria Gonzalez"
                value={borrowerName}
                onChange={(e) => setBorrowerName(e.target.value)}
                autoFocus
              />
            </div>

            {/* Loan Program Selection */}
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Loan Program</label>

              {isLoadingPrograms ? (
                <div className={styles.loadingPrograms}>
                  <div className={styles.spinner} />
                  Loading loan programs...
                </div>
              ) : (
                <div className={styles.programGrid}>
                  {loanPrograms.map((program) => {
                    const supported = isSupported(program.value)
                    const display = getDisplayInfo(program)
                    const isSelected = selectedProgram === program.value

                    return (
                      <button
                        key={program.value}
                        type="button"
                        className={`
                          ${styles.programCard}
                          ${isSelected ? styles.programCardSelected : ''}
                          ${!supported ? styles.programCardDisabled : ''}
                        `}
                        onClick={() => handleProgramClick(program.value)}
                      >
                        <div className={styles.programHeader}>
                          <span className={styles.programName}>{display.name}</span>
                          {isSelected && supported && (
                            <div className={styles.checkIcon}>
                              <Check size={14} />
                            </div>
                          )}
                          {!supported && (
                            <div className={styles.comingSoonBadge}>
                              <Clock size={12} />
                              Soon
                            </div>
                          )}
                        </div>
                        <span className={styles.programDescription}>{display.description}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className={styles.errorMessage}>
                {error}
              </div>
            )}

            {/* Submit Button */}
            <div className={styles.actions}>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting || !borrowerName.trim() || isLoadingPrograms}
              >
                {isSubmitting ? 'Creating Case...' : 'Create Case'}
              </button>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleBack}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Shell>
  )
}

export default NewCasePage
